/**
 * Catálogo de cursos da plataforma.
 *
 * Fonte da verdade da VITRINE (o que aparece na home pública). O banco continua
 * sendo a fonte da verdade do CONTEÚDO: cada curso publicado aponta para um
 * `topicId` de `topics`, e o merge em `mergeCourses.ts` cruza os dois.
 *
 * Por que estático: `topics` só tem (id, name) — não há capa, descrição, ordem
 * nem status de publicação no schema. Enquanto essas colunas não existirem, o
 * material de vitrine mora aqui. O custo é que publicar um curso novo exige
 * deploy do frontend.
 *
 * A vitrine mostra apenas cursos que existem de fato: uma entrada sem `topicId`
 * (ou cujo topic não está no banco) é filtrada em `usePlatformCourses`, em vez de
 * virar card "em breve". Anunciar catálogo que não existe é promessa falsa ao
 * visitante — com um curso publicado, a home mostra um card.
 */

export type CourseBadge = 'new' | 'soon';

/**
 * Marcador de "a arte ainda não chegou".
 *
 * Um curso pode entrar na vitrine antes de ter logo — foi o caso de
 * radiologia-r1 — e o placeholder é o que ocupa o lugar. Ele é uma imagem de
 * conteúdo genérica, então serve num card pequeno mas não numa tela inteira:
 * é `hasBrandArtForDarkScreens` que separa os dois usos, em vez de cada tela
 * repetir o caminho literal e errar sozinha.
 */
export const PLACEHOLDER_LOGO = '/assets/placeholder.svg';

export interface PlatformCourse {
  /** Chave estável de UI (e futura rota /curso/:slug). Não mudar depois de publicado. */
  slug: string;
  /** Id em `topics`. Ausente ⇒ curso ainda não existe no banco ("em breve"). */
  topicId?: number;
  /** Nome exibido. Tem precedência sobre o `name` vindo de /topics. */
  title: string;
  /** Linha de apoio no card. Ex.: "9 módulos · 24 aulas". */
  subtitle?: string;
  /** Capa retrato (~3:4). Ausente ⇒ o card usa `gradient`. */
  cover?: string;
  /**
   * Marca do curso, quadrada. Repete o `branding.logo` do manifesto porque a
   * tela de seleção de matrícula lista vários cursos e não pode carregar o
   * pacote de cada um só para desenhar um ícone. Um teste garante que os dois
   * não divirjam.
   */
  logo?: string;
  /**
   * A arte de `logo` pede fundo claro. As telas fora de curso — entrada e
   * seleção de matrícula — são azul-marinho, onde um logotipo de texto escuro
   * some; lá o curso cai na marca da plataforma. Ausente ⇒ a arte serve em
   * fundo escuro, como a dos cursos de tema escuro.
   */
  logoForLightBackground?: boolean;
  /** Classes de gradiente do placeholder, no formato Tailwind `from-* to-*`. */
  gradient: string;
  badge?: CourseBadge;
  /** Ordem na sidebar e no carrossel (crescente). */
  order: number;
  /** Curso exibido no hero. Apenas um deve marcar true. */
  featured?: boolean;
  /** Imagem de fundo do hero (só faz sentido junto com `featured`). */
  heroImage?: string;
  /**
   * Produto fora da plataforma. O card vira um link para cá, aberto na aba
   * atual: não há topic, pacote nem matrícula a verificar, então ele nunca é
   * "em breve" nem leva cadeado.
   */
  externalUrl?: string;
}

/**
 * Gradientes dos cards sem capa, na sequência do mockup do designer
 * (frontend/catalogo_1.html). Variados de propósito: conforme o catálogo
 * crescer, a fileira alterna tons e ganha ritmo visual.
 *
 * Exportado porque a fileira "continue de onde parou" reusa a mesma paleta para
 * módulos que ainda não têm capa cadastrada.
 */
export const GRADIENTS = [
  'from-mv-surface-2 to-mv-accent',
  'from-mv-bg to-mv-violet',
  'from-mv-surface to-[#8a1a9c]',
  'from-[#241a52] to-mv-accent',
  'from-[#101a3d] to-[#3a1f6e]',
  'from-[#2a1b4d] to-mv-accent-hi',
  'from-mv-surface to-[#4a1470]',
];

export const PLATFORM_COURSES: PlatformCourse[] = [
  {
    slug: 'neurodidatica',
    // topic 1 = Neuroanatomia, confirmado pelos seeds 036/038 e por
    // SCHEDULE_LESSONS_TOPIC_ID em App.tsx.
    topicId: 1,
    title: 'Neurodidática',
    subtitle: 'Neuroanatomia aplicada',
    cover: '/assets/home_courses/01_neurodidatica.jpeg',
    logo: '/assets/logos/01-NeuroDidatica-RadExperts.png',
    gradient: GRADIENTS[0],
    badge: 'new',
    order: 1,
    featured: true,
  },
  {
    slug: 'radiologia-r1',
    // topic 2001 = Radiologia R1, criado pela migration 040 com id reservado.
    // Id alto de propósito: a faixa baixa pertence ao SERIAL de `topics`.
    topicId: 2001,
    title: 'Radiologia R1',
    subtitle: 'Banco de questões e curadoria de casos',
    // A capa é a arte RadExperts Academy recomposta em retrato: a original é
    // quadrada, e o `object-cover` do card cortaria as pontas do logotipo.
    cover: '/assets/home_courses/02_radiologia_r1.jpg',
    // Precisa bater com o branding.logo do manifesto (verificado por
    // tests/courseManifests.test.ts).
    logo: '/assets/logos/02-RadExperts-Academy.png',
    // Texto escuro sobre transparente: desenhado para o tema claro do curso.
    logoForLightBackground: true,
    gradient: GRADIENTS[1],
    badge: 'new',
    order: 2,
  },
  {
    slug: 'angio-tc',
    // topic 2003, criado pela migration 074 com id reservado: a metade de
    // Angio-TC do antigo Scan Expert. Mesma marca e mesma linha de apoio do
    // curso de RM até o cliente mandar as próprias.
    topicId: 2003,
    title: 'Imersão em Angio-TC das Artérias Pulmonares',
    subtitle: 'TC e RM para técnicos e biomédicos',
    logo: '/assets/logos/scan-expert.png',
    gradient: GRADIENTS[4],
    badge: 'new',
    order: 3,
  },
  {
    // Slug do antigo Scan Expert, mantido: já está publicado (URL, links de
    // /entrar?curso=). topic 2002, criado pela migration 061 com id reservado e
    // renomeado pela 073 quando o Scan Expert se dividiu em dois cursos.
    slug: 'scan-expert',
    topicId: 2002,
    title: 'Imersão em RM do básico ao avançado',
    subtitle: 'TC e RM para técnicos e biomédicos',
    // A capa é a foto dos professores recomposta em retrato: a original é
    // paisagem, e o `object-cover` do card deixaria só um deles à vista.
    cover: '/assets/home_courses/03_imersao_rm_retrato.jpg',
    logo: '/assets/logos/scan-expert.png',
    gradient: GRADIENTS[2],
    badge: 'new',
    order: 4,
  },
  {
    slug: 'neuronews',
    // Produto separado, hospedado fora da plataforma: sem topicId nem pacote.
    title: 'NeuroNews',
    subtitle: 'Atualizações em neurorradiologia',
    externalUrl: 'https://neuronews.app-radexperts.com.br/',
    gradient: GRADIENTS[3],
    badge: 'new',
    order: 5,
  },
];

/**
 * Índice síncrono slug ↔ topicId.
 *
 * Os guards de rota decidem antes de qualquer await — carregar o pacote de um
 * curso só para descobrir de qual curso a URL fala anularia a divisão em
 * chunks. O catálogo já é estático e já está em memória, então é ele que
 * responde. Nenhuma das duas compara com literal: recebem o que procuram.
 */
export function findCourseBySlug(slug: string): PlatformCourse | undefined {
  return PLATFORM_COURSES.find((course) => course.slug === slug);
}

/**
 * O curso tem logo de verdade que se lê nas telas escuras de fora do curso.
 *
 * Quem exibe a marca em tamanho grande (a tela de entrada) precisa saber a
 * diferença: um placeholder cinza de 640×360 no lugar de uma logo diz menos
 * do que a marca da plataforma, e um logotipo feito para fundo claro nem
 * aparece sobre o marinho.
 */
export function hasBrandArtForDarkScreens(course: PlatformCourse | undefined): boolean {
  return (
    course != null &&
    course.logo != null &&
    course.logo !== PLACEHOLDER_LOGO &&
    !course.logoForLightBackground
  );
}

export function findCourseByTopicId(
  topicId: number | undefined,
): PlatformCourse | undefined {
  if (topicId == null) return undefined;
  return PLATFORM_COURSES.find((course) => course.topicId === topicId);
}
